import _ from 'lodash';
import { headlessCaptureMetadata, headlessRenderMode } from '@/services/headlessCapture.utilities';
import { getBrowserUrl } from '@/utilities/browserLocation.utilities';
import { SeeqNames } from '@seeqdev/constants';

export function getOrigin({
  isTopic,
  isVantage,
  isAnalysis,
}: {
  isTopic: boolean;
  isVantage: boolean;
  isAnalysis: boolean;
}) {
  if (headlessRenderMode()) {
    return headlessCaptureMetadata().category ?? '';
  } else if (isTopic) {
    return SeeqNames.Consumption.Topic;
  } else if (isVantage) {
    return SeeqNames.Consumption.Vantage;
  } else if (isAnalysis) {
    return SeeqNames.Origins.Analysis;
  } else if (getBrowserUrl().pathname.startsWith('/administration/')) {
    return SeeqNames.Consumption.Administration;
  }
  return '';
}

/**
 * This will retrieve the OriginURL used for Data Consumption metric tracking. The logic for determining the url:
 * 1. Check to see if an OriginURL parameter is set that overrides anything else. The OriginURL is used to set the
 * URL for Content that is being rendered.
 * 2. Construct the URL as the view-only URL for the current Analysis, Vantage, or Topic.
 * 3. Use the window.location
 */
export function getOriginUrl({
  originURL: maybeOriginOverride,
  workbookId: workbookGuid,
  worksheetId: worksheetGuid,
}: {
  originURL: string | undefined;
  workbookId: string | undefined;
  worksheetId: string | undefined;
}): string {
  // If this is changed, make sure to update `ReportNotifierJob::runJob`'s construction of its url
  const viewOnlyUrl =
    !_.isNil(workbookGuid) && !_.isNil(worksheetGuid) ? `/view/worksheet/${workbookGuid}/${worksheetGuid}` : null;
  const windowUrl = getBrowserUrl().pathname + getBrowserUrl().search;
  return maybeOriginOverride || viewOnlyUrl || windowUrl;
}

/**
 * This will retrieve the OriginLabel used for Data Consumption metric tracking. The logic for determining the label:
 * 1. Check to see if an originLabel parameter is set that overrides anything else. The originLabel is used to set the
 * label for Content that is being rendered. For more info:
 * https://seeq.atlassian.net/wiki/spaces/SQ/pages/2355462313/CRAB-29167+Data+Consumption+OriginLabel+and+OriginURL+Tracking
 * 2. Construct the label from the workbook name and worksheet name, which can be an Analysis, Topic, or Vantage room.
 * 3. Use the document title
 */
export function getOriginLabel({
  originLabel: maybeOriginOverride,
  workbookName,
  worksheetName,
  isLocked,
}: {
  originLabel: string | undefined;
  workbookName: string | undefined;
  worksheetName: string | undefined;
  isLocked: boolean;
}): string {
  const fallbackLabel = document.title.replace(/ - Seeq$/, '');
  const originLabel =
    maybeOriginOverride ||
    _.chain([workbookName, worksheetName])
      .reject(_.isEmpty)
      .thru((parts) => (parts.length === 2 ? parts.join(' - ') : fallbackLabel))
      .value();

  return encodeURIComponent(originLabel.length > 0 && isLocked ? `${originLabel} [Locked]` : originLabel);
}
